import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Paper,
  Select,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const EMPTY_ARRAY = [];

export default function RolePermissionMatrix() {
  // ============================================================
  // SESSION
  // ============================================================

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const clientId = Number(user.client_id || 0);

  const collegeId = Number(user.college_id || 0);

  const userId = Number(user.user_id || 0);

  // ============================================================
  // STATE
  // ============================================================

  const [roles, setRoles] = useState([]);

  const [selectedRoleId, setSelectedRoleId] = useState("");

  const [matrix, setMatrix] = useState([]);

  const [loadingRoles, setLoadingRoles] = useState(false);

  const [loadingMatrix, setLoadingMatrix] = useState(false);

  const [saving, setSaving] = useState(false);

  const [expandedModules, setExpandedModules] = useState({});

  const [expandedTabs, setExpandedTabs] = useState({});

  const [expandedButtons, setExpandedButtons] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // ============================================================
  // LOAD ROLES
  // ============================================================

  useEffect(() => {
    if (!clientId || !collegeId) {
      return;
    }

    loadRoles();
  }, [clientId, collegeId]);

  const loadRoles = async () => {
    try {
      setLoadingRoles(true);

      const response = await axios.get(
        `${API_BASE}/api/rbacall/roles/all/${clientId}/${collegeId}`
      );

      const result = response.data;

      const items = result?.data?.items || result?.data || result?.items || result?.rows || [];

      const roleList = Array.isArray(items) ? items : [];

      setRoles(roleList);

      if (roleList.length > 0) {
        setSelectedRoleId(String(roleList[0].role_id));
      }
    } catch (error) {
      console.error("loadRoles:", error);

      showMessage("Failed to load roles", "error");
    } finally {
      setLoadingRoles(false);
    }
  };

  // ============================================================
  // LOAD MATRIX
  // ============================================================

  useEffect(() => {
    if (!clientId || !collegeId || !selectedRoleId) {
      setMatrix([]);
      return;
    }

    loadMatrix();
  }, [clientId, collegeId, selectedRoleId]);

  const loadMatrix = async () => {
    try {
      setLoadingMatrix(true);

      const response = await axios.get(
        `${API_BASE}/api/rbacall/role-permission-matrix/${clientId}/${collegeId}/${selectedRoleId}`
      );

      const result = response.data;

      const modules = result?.data?.modules || result?.modules || [];

      setMatrix(Array.isArray(modules) ? modules : []);

      // Expand all modules initially
      const moduleState = {};

      const tabState = {};

      const buttonState = {};

      modules.forEach((module) => {
        moduleState[String(module.module_id)] = true;

        (module.tabs || []).forEach((tab) => {
          tabState[String(tab.module_tab_id)] = true;

          (tab.buttons || []).forEach((button) => {
            buttonState[String(button.module_button_id)] = true;
          });
        });
      });

      setExpandedModules(moduleState);
      setExpandedTabs(tabState);
      setExpandedButtons(buttonState);
    } catch (error) {
      console.error("loadMatrix:", error);

      setMatrix([]);

      showMessage("Failed to load permission matrix", "error");
    } finally {
      setLoadingMatrix(false);
    }
  };

  // ============================================================
  // SNACKBAR
  // ============================================================

  const showMessage = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // ============================================================
  // MODULE CHECKBOX
  //
  // MODULE controls everything below it.
  // ============================================================

  const handleModuleChange = (moduleIndex, checked) => {
    setMatrix((previous) => {
      const updated = structuredClone(previous);

      const module = updated[moduleIndex];

      module.is_allowed = checked;

      (module.tabs || []).forEach((tab) => {
        tab.is_allowed = checked;

        (tab.buttons || []).forEach((button) => {
          button.is_allowed = checked;

          (button.actions || []).forEach((action) => {
            action.is_allowed = checked;
          });
        });
      });

      return updated;
    });
  };

  // ============================================================
  // TAB CHECKBOX
  //
  // TAB controls only itself + children.
  // It DOES NOT change module.
  // ============================================================

  const handleTabChange = (moduleIndex, tabIndex, checked) => {
    setMatrix((previous) => {
      const updated = structuredClone(previous);

      const tab = updated[moduleIndex].tabs[tabIndex];

      tab.is_allowed = checked;

      (tab.buttons || []).forEach((button) => {
        button.is_allowed = checked;

        (button.actions || []).forEach((action) => {
          action.is_allowed = checked;
        });
      });

      return updated;
    });
  };

  // ============================================================
  // BUTTON CHECKBOX
  //
  // BUTTON controls itself + actions.
  // It DOES NOT change tab/module.
  // ============================================================

  const handleButtonChange = (moduleIndex, tabIndex, buttonIndex, checked) => {
    setMatrix((previous) => {
      const updated = structuredClone(previous);

      const button = updated[moduleIndex].tabs[tabIndex].buttons[buttonIndex];

      button.is_allowed = checked;

      (button.actions || []).forEach((action) => {
        action.is_allowed = checked;
      });

      return updated;
    });
  };

  // ============================================================
  // ACTION CHECKBOX
  //
  // ACTION changes ONLY itself.
  // ============================================================

  const handleActionChange = (moduleIndex, tabIndex, buttonIndex, actionIndex, checked) => {
    setMatrix((previous) => {
      const updated = structuredClone(previous);

      updated[moduleIndex].tabs[tabIndex].buttons[buttonIndex].actions[actionIndex].is_allowed =
        checked;

      return updated;
    });
  };

  // ============================================================
  // SAVE
  // ============================================================

  const savePermissions = async () => {
    if (!selectedRoleId) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Please select a role.",
      });
      return;
    }

    try {
      setSaving(true);

      const permissions = [];

      matrix.forEach((module) => {
        // ============================================================
        // MODULE
        // ============================================================
        if (module.is_allowed) {
          permissions.push({
            permission_type: "MODULE",
            module_id: Number(module.module_id),
            is_allowed: true,
          });
        }

        // ============================================================
        // TABS
        // ============================================================
        (module.tabs || []).forEach((tab) => {
          if (tab.is_allowed) {
            permissions.push({
              permission_type: "TAB",
              module_id: Number(module.module_id),
              tab_id: Number(tab.module_tab_id),
              is_allowed: true,
            });
          }

          // ==========================================================
          // BUTTONS
          // ==========================================================
          (tab.buttons || []).forEach((button) => {
            if (button.is_allowed) {
              permissions.push({
                permission_type: "BUTTON",
                module_id: Number(module.module_id),
                tab_id: Number(tab.module_tab_id),
                button_id: Number(button.module_button_id),
                is_allowed: true,
              });
            }

            // ========================================================
            // ACTIONS
            // ========================================================
            (button.actions || []).forEach((action) => {
              if (action.is_allowed) {
                permissions.push({
                  permission_type: "ACTION",
                  module_id: Number(module.module_id),
                  tab_id: Number(tab.module_tab_id),
                  button_id: Number(button.module_button_id),
                  action_id: Number(action.button_action_id),
                  is_allowed: true,
                });
              }
            });
          });
        });
      });

      const response = await axios.put(
        `${API_BASE}/api/rbacall/role-permission-matrix/${clientId}/${collegeId}/${selectedRoleId}`,
        {
          role_id: Number(selectedRoleId),
          client_id: Number(clientId),
          college_id: Number(collegeId),
          created_by: userId ? Number(userId) : null,
          permissions,
        }
      );

      if (response.data?.success) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Role permissions saved successfully.",
        });

        await loadMatrix(selectedRoleId);
      }
    } catch (error) {
      console.error("SAVE ROLE PERMISSIONS ERROR:", error);

      setSnackbar({
        open: true,
        severity: "error",
        message: error.response?.data?.message || "Failed to save role permissions.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // EXPAND / COLLAPSE
  // ============================================================

  const toggleModule = (moduleId) => {
    const key = String(moduleId);

    setExpandedModules((prev) => {
      const isCurrentlyExpanded = Boolean(prev[key]);

      // Clicking the already-open module → close it
      if (isCurrentlyExpanded) {
        return {};
      }

      // Open this module and close every other module
      return {
        [key]: true,
      };
    });
  };

  const toggleTab = (tabId) => {
    setExpandedTabs((previous) => ({
      ...previous,
      [String(tabId)]: !previous[String(tabId)],
    }));
  };

  const toggleButton = (buttonId) => {
    setExpandedButtons((previous) => ({
      ...previous,
      [String(buttonId)]: !previous[String(buttonId)],
    }));
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
      }}
    >
      {/* ======================================================
          HEADER
      ======================================================= */}

      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2}
          alignItems={{
            xs: "stretch",
            sm: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              flex: 1,
            }}
          >
            ROLE PERMISSION/ACTION STRUCTURE
          </Typography>

          <FormControl
            size="small"
            sx={{
              minWidth: 280,
            }}
          >
            <InputLabel shrink>Role</InputLabel>

            <Select
              native
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              label="Role"
            >
              <option value="">Select Role</option>

              {roles.map((role) => (
                <option key={role.role_id} value={role.role_id}>
                  {role.role_name}
                  {role.role_code ? ` (${role.role_code})` : ""}
                </option>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={saving || loadingMatrix || !selectedRoleId}
            onClick={savePermissions}
            sx={{ color: "#fff" }}
          >
            {saving ? "Saving..." : "Save Permissions"}
          </Button>
        </Stack>
      </Paper>

      {/* ======================================================
          MATRIX
      ======================================================= */}

      <Paper
        elevation={2}
        sx={{
          overflow: "hidden",
        }}
      >
        {loadingRoles || loadingMatrix ? (
          <Box
            sx={{
              py: 8,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : !selectedRoleId ? (
          <Box
            sx={{
              p: 5,
              textAlign: "center",
            }}
          >
            <Typography>Please select a role.</Typography>
          </Box>
        ) : matrix.length === 0 ? (
          <Box
            sx={{
              p: 5,
              textAlign: "center",
            }}
          >
            <Typography>No modules found.</Typography>
          </Box>
        ) : (
          matrix.map((module, moduleIndex) => {
            const moduleExpanded = expandedModules[String(module.module_id)] === true;

            return (
              <Box key={module.module_id}>
                {/* =====================================================
              MODULE
          ====================================================== */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    backgroundColor: "rgba(25,118,210,0.08)",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {/* MODULE CHECKBOX */}
                  <Checkbox
                    size="small"
                    checked={Boolean(module.is_allowed)}
                    onChange={(e) => handleModuleChange(moduleIndex, e.target.checked)}
                  />

                  {/* + / - */}
                  <Button
                    size="small"
                    onClick={() => toggleModule(module.module_id)}
                    sx={{
                      minWidth: 32,
                      width: 32,
                      height: 32,
                      p: 0,
                      mr: 1,
                      fontSize: 20,
                      fontWeight: 700,
                    }}
                  >
                    {moduleExpanded ? "−" : "+"}
                  </Button>

                  {/* MODULE NAME */}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      flex: 1,
                    }}
                  >
                    {module.module_name}
                  </Typography>

                  {/* STATUS */}
                  <Typography
                    variant="caption"
                    sx={{
                      mr: 2,
                      color: module.is_allowed ? "success.main" : "text.secondary",
                      fontWeight: 600,
                    }}
                  >
                    {module.is_allowed ? "Allowed" : "Not Allowed"}
                  </Typography>
                </Box>

                {/* =====================================================
              EVERYTHING UNDER MODULE
          ====================================================== */}
                {moduleExpanded && (
                  <Box>
                    {(module.tabs || []).map((tab, tabIndex) => (
                      <Box key={tab.module_tab_id}>
                        {/* =================================================
                        TAB
                    ================================================== */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            py: 0.75,
                            pl: 6,
                            backgroundColor: "rgba(0,0,0,0.015)",
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={Boolean(tab.is_allowed)}
                            onChange={(e) =>
                              handleTabChange(moduleIndex, tabIndex, e.target.checked)
                            }
                          />

                          <Typography
                            sx={{
                              fontWeight: 600,
                              flex: 1,
                            }}
                          >
                            {tab.tab_name}
                          </Typography>
                        </Box>

                        {/* =================================================
                        BUTTONS
                    ================================================== */}
                        {(tab.buttons || []).map((button, buttonIndex) => (
                          <Box key={button.module_button_id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                px: 2,
                                py: 0.5,
                                pl: 10,
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={Boolean(button.is_allowed)}
                                onChange={(e) =>
                                  handleButtonChange(
                                    moduleIndex,
                                    tabIndex,
                                    buttonIndex,
                                    e.target.checked
                                  )
                                }
                              />

                              <Typography
                                sx={{
                                  flex: 1,
                                }}
                              >
                                {button.button_name}
                              </Typography>
                            </Box>

                            {/* =================================================
                              ACTIONS
                          ================================================== */}
                            {(button.actions || []).map((action, actionIndex) => (
                              <Box
                                key={action.button_action_id}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  px: 2,
                                  py: 0.25,
                                  pl: 14,
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={Boolean(action.is_allowed)}
                                  onChange={(e) =>
                                    handleActionChange(
                                      moduleIndex,
                                      tabIndex,
                                      buttonIndex,
                                      actionIndex,
                                      e.target.checked
                                    )
                                  }
                                />

                                <Typography
                                  sx={{
                                    flex: 1,
                                  }}
                                >
                                  {action.action_name}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    mr: 2,
                                    color: "text.secondary",
                                    minWidth: 60,
                                    textAlign: "right",
                                  }}
                                >
                                  {action.http_method}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}

                <Divider />
              </Box>
            );
          })
        )}
      </Paper>

      {/* ======================================================
          SNACKBAR
      ======================================================= */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar((previous) => ({
              ...previous,
              open: false,
            }))
          }
          sx={{
            backgroundColor: "#05752c",
            color: "#fff",
            fontWeight: 500,
            "& .MuiAlert-icon": {
              color: "#fff",
            },
            "& .MuiAlert-action": {
              color: "#fff",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
